/**
 * **The Zeppelin shoots back.** `MG.DAT`, decoded — the airship's own copy of
 * the `<PLANE>.DAT` gun-parameter block, its four gun stations, and everything
 * a station needs to be brought onto a target.
 *
 * The evidence is [docs/engineering/zeppelin-guns.md](../../../../docs/engineering/zeppelin-guns.md);
 * nothing here re-derives it. `MG.DAT` is 100 bytes at `VOLUME.002` offset 3609,
 * SHA-256 `b64b8dd7…43bcf`, and it is the **only** source of airship gun data in
 * either build — there is no `ZEPPELIN.DAT`. Its layout is the aircraft one field
 * for field: 13 `int16` bullet globals, then `gunParams`, then
 * `gunParams[+0x0a]` 16-byte station records in the exact
 * `aircraft-gun-table.ts` `GunBlockRow` shape.
 *
 * The manual is the reason to believe the decode rather than the other way
 * round:
 *
 * > *"fly behind the Zeppelin and attack it from the rear. This shields the
 * > plane from the German gunners and their machine guns positioned above and
 * > below."* — docs/game/manual/03-flight-reference.md:401
 *
 * Two on the crown, two under the hull, and **all four elevation arcs exclude
 * zero** — `traverse.ts`'s flexible-gunner signature. Stations 1 and 3 are
 * shaped exactly like the Gotha's dorsal and tunnel guns: wrapping azimuth,
 * one-sided elevation. So the level rear is a hole in the cover, which is what
 * the manual has been telling the player to fly into since 1990.
 *
 * ## What is VERIFIED and what is not
 *
 * The **numbers** in `ZEPPELIN_GUN_BLOCKS`, `ZEPPELIN_GUN_EFFECTIVENESS`,
 * `ZEPPELIN_ROUNDS_PER_STATION` and `ZEPPELIN_ENGAGEMENT_RANGE_M` are read
 * straight out of the shipped file: **VERIFIED**.
 *
 * How `RB.EXE` *spends* them — the `kind == 1` selection in `mg.c`, the
 * dedicated `shooting.c+0x534` per-tick loop over airships × every aircraft,
 * the absence of any mission/difficulty/damage/day-night gate — is
 * **REPORTED**: recovered from a static disassembly, not traced live. Where
 * this port follows the reported engagement loop it says so at the point it
 * does (`airship-pass.ts`).
 *
 * ## Model units, and why the mounts are placed proportionally (§4.1, decided)
 *
 * `aircraft-gun-table.ts` reads aircraft gun positions in the **model frame**
 * (≈65 units/m), and the Bristol forces that reading for the fleet. The
 * Zeppelin cannot discriminate on its own — both a model reading and a world
 * reading put its guns plausibly just proud of the skin — so it follows the
 * family: **model frame**, `MODEL_UNITS_PER_METRE`, same as every aeroplane.
 *
 * But it does **not** hard-code the resulting metres. Each mount is carried as a
 * *fraction of the model's own bounding box* and multiplied back out by the
 * hull the port actually draws, because §4.2 of the spec is open: the baked
 * gasbag measures 96.9 × 10.8 × 11.7 m against a real L-30 of 198 × 24 m and a
 * recovered collision hull spanning 217 m, and ×2 and ×2.4765 both have
 * support. Gun mounts on a hull of the wrong size are meaningless, so they are
 * expressed in a form that has no opinion: whatever `ZEPPELIN_GASBAG_SCALE`
 * becomes, the crown guns stay on the crown and the keel guns stay on the keel.
 *
 * **That scale is emphatically NOT the terrain fix.** The ×2.4765 correction
 * `london-sea-placements.md` §7.4 proves for scenery is a `scaleExponent > 0`
 * rule; `ZEPPELIN.TBL` object 0's `classFlags` low byte is 0, so the Zeppelin,
 * the balloon and all 33 aircraft are outside it. Nothing here is rebaked on
 * that evidence.
 */

import { MODEL_UNITS_PER_METRE } from "../flight/aircraft-model-frame.ts";
import type { GunBlockRow } from "../flight/aircraft-gun-table.ts";
import { barrelsForMountClass } from "../flight/aircraft-gun-table.ts";
import type { GunMountSpec, GunSpec } from "../flight/guns.ts";
import type { Vec3 } from "../flight/math3d.ts";
import { bamToRadians } from "../flight/units.ts";
import type { TargetKind } from "./target-kinds.ts";

/**
 * The kinds that carry `MG.DAT`'s guns. One member, and the balloon is
 * deliberately not it: `pobj.c`'s balloon constructor (`+0x537`) never
 * allocates the per-station records the airship constructor (`+0x317`) does, so
 * a tethered gasbag is unarmed in the original and unarmed here. REPORTED.
 *
 * A list rather than a `kind === "zeppelin"` test so the one place that decides
 * "does this shoot back" is named, the way `DEFENCE_TARGET_KINDS` names the one
 * place that decides "does this belong to a battery".
 */
export const ARMED_TARGET_KINDS = ["zeppelin"] as const;

export type ArmedTargetKind = (typeof ARMED_TARGET_KINDS)[number];

export function isArmedTargetKind(kind: TargetKind): kind is ArmedTargetKind {
  return (ARMED_TARGET_KINDS as readonly TargetKind[]).includes(kind);
}

/**
 * `MG.DAT` `0x00`–`0x19` — the 13 `int16` bullet globals, verbatim, in file
 * order. Three are named by `gun-mounts-design.md` §9.4 and one by the lead
 * solver; the rest are carried because a partial transcription of a 100-byte
 * file is how a later lane ends up re-opening it.
 */
export const ZEPPELIN_BULLET_GLOBALS: readonly number[] = [
  18000, 2500, 3500, 250, 1000, 1212, 500, 300, 350, 400, 5000, 4000, 4500,
];

/**
 * World units per metre for the airship's own frame — `65 / 2.4765`, the ratio
 * `zeppelin-guns.md` §4.1 tabulates. Used only to convert `+0 = 18000` into the
 * engagement range; the mounts themselves are model units (see the module note).
 */
const WORLD_UNITS_PER_METRE = 26.2467;

/**
 * `MG.DAT` `+0` = 18000 world units. The range gate `shooting.c` applies before
 * it asks whether any station bears: past this the airship does not engage at
 * all. VERIFIED as a value.
 *
 * It lands 14 m inside the port's own `gunMaxRangeM` of 700, which is a
 * coincidence worth stating rather than leaning on — an airship therefore opens
 * fire at very nearly the longest range at which its rounds can still do
 * anything, and the whole approach is flown under fire.
 */
export const ZEPPELIN_ENGAGEMENT_RANGE_M = ZEPPELIN_BULLET_GLOBALS[0]! / WORLD_UNITS_PER_METRE;

/**
 * `MG.DAT` `0x1A` — `gunParams[+0x00]`, gun effectiveness. **200**, against a
 * fighter's 800–1300 (`CAMEL`/`BRISTOL` 1300, `GOTHA` 1024) and the port's
 * `gunEffectivenessReference` of 1024. The airship's gunners hit at roughly a
 * fifth of a Camel's weight per round, which is the single number that keeps
 * four stations from being an execution. VERIFIED.
 */
export const ZEPPELIN_GUN_EFFECTIVENESS = 200;

/**
 * `MG.DAT` `0x1C/0x1E/0x20` — `gunParams[+0x04/+0x06/+0x08]`, the jam-threshold
 * triple, byte-identical to every aircraft's `.DAT` 210/212/214. Carried as
 * reference data and **not consumed**, exactly as `GunSpec.jamThresholdTable`
 * is not: jams are gated on the player object in the original, and an airship
 * is not one.
 */
export const ZEPPELIN_JAM_THRESHOLD_TABLE: readonly [number, number, number] = [40, 20, 30];

/**
 * `pobj.c+0x317` seeds every station's 4-byte record with `0x78` = 120 rounds
 * at spawn. REPORTED (disassembly), and the port takes it literally: a station
 * that empties stays empty for the rest of the encounter, because nothing in
 * the original reloads one.
 *
 * That finiteness is doing real work. Four stations × 120 is 480 rounds for the
 * whole sortie; an airship that spends them on the first pass has nothing left
 * for the second, which is what makes a patient attack different from a
 * head-down one.
 */
export const ZEPPELIN_ROUNDS_PER_STATION = 120;

/**
 * The graze scatter, `±(rand() & 0x7f)`, read as **BAM16 counts on the aim
 * angle**: ±127 counts = ±0.698° on each axis.
 *
 * ## Why that reading, stated, because the spec does not settle it
 *
 * `shooting.c` perturbs "the aim point" on a graze result. Two readings are
 * available and they behave completely differently:
 *
 * - **World units on the aim point.** 127 units is 4.84 m, applied at whatever
 *   range the target is. That is *range-independent in metres*, so it is a
 *   tighter angle the further away the target is — an airship would be about
 *   equally lethal at 650 m and at 80 m, and closing would buy the attacker
 *   nothing.
 * - **BAM16 counts on the aim angle**, which is what every other angular field
 *   in this block is in, and which has an exact sibling: `gunIncendiaryJitterBam`
 *   is `0…63` counts of one-sided aim perturbation, RECOVERED from the same
 *   `mg.c`. `0x7f` is `2 × 63 + 1` — a two-sided version of the same mask, one
 *   bit wider.
 *
 * The port takes the second. It is the coherent reading of the two recovered
 * perturbations, and it is the only one that leaves range meaningful.
 *
 * **And it is carrying more weight than it did in the original**, which must be
 * said plainly: `shooting.c` rolled to hit *first* and only scattered on a
 * graze. This port has no hit roll anywhere — `collide.ts` is geometry and only
 * geometry (that was AD-007g's whole correction) — so the scatter is the
 * airship's entire accuracy model rather than the tail of it. Porting the graze
 * scatter without the roll it qualified would give a gunner with a lead
 * solution a perfect line; this is the honest substitution, and it is
 * CALIBRATION-PENDING in exactly the way the rest of the gunnery constants are.
 */
export const ZEPPELIN_GRAZE_SCATTER_BAM = 0x7f;

/**
 * Fraction of the player's rate of fire an AI-driven station manages.
 *
 * `mg.c`'s per-station rate accumulator fires when it crosses `0xff` and is
 * reset to `0xab` for the player and `0` for anything else
 * (`gun-mounts-design.md` §4), so an AI station needs a full 256 counts of
 * accumulation where the player needs 85 — the player fires three times as
 * fast for the same increment. REPORTED.
 *
 * Applied to the port's flat `gunRoundsPerSecond` of 55 it gives ~18 rounds/s
 * per station. Both numbers are volume-of-fire abstractions rather than cyclic
 * rates — 55/s is 3300 rpm, which no gun on either side of the war approached;
 * it is what Mobley's "≈300 rounds in 5–6 s" footage calibrates the *player's*
 * gun to, and the fleet has run on it since. What this constant claims is only
 * the **ratio**, which is the part `mg.c` actually states.
 *
 * `gun-mounts-design.md` §7 already flags the player/AI rate split as wanting a
 * lane of its own against the port's single flat rate. This applies it to the
 * airship only, because the airship is new and has no established behaviour to
 * move; it does **not** touch what any aeroplane fires at.
 */
export const AIRSHIP_RATE_OF_FIRE_FRACTION = (0x100 - 0xab) / 0x100;

/**
 * `MG.DAT` `0x24`–`0x63` — four 16-byte gun blocks, verbatim, in file order and
 * in the same column order `AIRCRAFT_GUN_BLOCKS` uses.
 *
 * | # | right | fwd | up | mount | elevation | azimuth |
 * |---|---|---|---|---|---|---|
 * | 0 | 0 | +1090 | +292 | 6 | −5.49°…+60.42° | ±131.84°, forward |
 * | 1 | 0 | −1020 | +292 | 6 | +5.49°…+60.42° | wraps rear, \|az\| ≥ 43.95° |
 * | 2 | 0 | +1000 | −453 | 6 | −43.95°…+5.49° | ±131.84°, forward |
 * | 3 | 0 | −1302 | −453 | 6 | −43.95°…−5.49° | wraps rear |
 *
 * Stations 0 and 1 are the crown pair, 2 and 3 the keel pair — *"guns mounted
 * in each carriage and guns mounted on the top of the Zeppelin"*, exactly.
 */
export const ZEPPELIN_GUN_BLOCKS: readonly GunBlockRow[] = [
  [0, 1090, 292, 6, -1000, 11000, -24000, 24000],
  [0, -1020, 292, 6, 1000, 11000, 8000, -8000],
  [0, 1000, -453, 6, -8000, 1000, -24000, 24000],
  [0, -1302, -453, 6, -8000, -1000, 8000, -8000],
];

/**
 * `ZEPPELIN.TBL` object 0's own bounding box, in model units — measured off
 * `data/baked/models/ZEPPELIN.json`'s highest-detail LOD exactly as
 * `MODEL_CENTRE_UNITS` is measured for the fleet, and multiplied back up by
 * `MODEL_UNITS_PER_METRE` because the baker writes metres.
 *
 * Nose-positive `forward`, up-positive `up`, matching the `.DAT` gun frame. The
 * `.TBL` origin sits well aft of centre (the box is −3654…+2643), which is why
 * the mounts are recentred before they are turned into fractions — the same
 * correction `aircraft-model-frame.ts` applies to every aeroplane.
 */
export const ZEPPELIN_MODEL_BOX_UNITS = {
  forwardMin: -3654,
  forwardMax: 2643,
  upMin: -483,
  upMax: 279,
  rightMax: 351,
} as const;

/**
 * The gasbag's overall length as this port currently draws it, metres —
 * `data/baked/models/ZEPPELIN` measured nose to tail.
 *
 * **§4.2 is OPEN and this constant does not close it.** The as-baked 96.9 m
 * fits no WWI airship (none was under 128 m); ×2 gives 193.8 m against the real
 * L-30's 198 m; ×2.4765 gives 239.9 m and fits the recovered collision hull's
 * *longitudinal* ratio better while fitting its transverse ratio worse. Both
 * candidates straddle the evidence and neither is settled, so nothing is
 * rebaked and nothing here picks a winner.
 *
 * What this file guarantees instead is that picking one later is a one-line
 * change: raise `ZEPPELIN_GASBAG_SCALE` and every mount moves with the skin,
 * because the mounts are fractions of this box rather than metres off it.
 */
export const ZEPPELIN_HULL_LENGTH_M =
  (ZEPPELIN_MODEL_BOX_UNITS.forwardMax - ZEPPELIN_MODEL_BOX_UNITS.forwardMin) / MODEL_UNITS_PER_METRE;

/** As drawn. See `ZEPPELIN_HULL_LENGTH_M` — the ×2 / ×2.4765 question lives here and nowhere else. */
export const ZEPPELIN_GASBAG_SCALE = 1;

/**
 * One mount's position as a **fraction of the hull**: ±1 is the skin fore and
 * aft, ±1 is the skin above and below. Pure arithmetic on the block and the
 * box, so a reader can check it against §2.1 without running anything.
 *
 * The four come out at forward `+0.507 / −0.163 / +0.478 / −0.253` and up
 * `+1.034` (crown) / `−0.921` (keel) — both within 8 % of the skin, which is
 * the check that the model-frame reading is the right one. `airship-guns`'s
 * test pins it.
 */
export function zeppelinMountFraction(block: GunBlockRow): { readonly forward: number; readonly up: number } {
  const box = ZEPPELIN_MODEL_BOX_UNITS;
  const forwardCentre = (box.forwardMin + box.forwardMax) / 2;
  const upCentre = (box.upMin + box.upMax) / 2;
  return {
    forward: (block[1] - forwardCentre) / ((box.forwardMax - box.forwardMin) / 2),
    up: (block[2] - upCentre) / ((box.upMax - box.upMin) / 2),
  };
}

/**
 * A station's muzzle in the airship's **body** frame (right +X, up +Y, forward
 * −Z), metres from the pose origin — the fraction above, carried back out onto
 * the hull the renderer actually draws.
 *
 * The `(x, y, z) → (x, z, −y)` axis map is the same one
 * `modelPointToBodyMetres` applies to an aeroplane, and the recentring is the
 * same recentring: `apps/web` puts a baked mesh on its own bounding-box centre,
 * so the pose origin is the box centre and not the `.TBL` origin.
 */
export function zeppelinMountOffsetM(block: GunBlockRow): Vec3 {
  const box = ZEPPELIN_MODEL_BOX_UNITS;
  const fraction = zeppelinMountFraction(block);
  const halfLengthM = (ZEPPELIN_HULL_LENGTH_M * ZEPPELIN_GASBAG_SCALE) / 2;
  // Height rides the same scale as length: the open question is a uniform
  // model scale, not an aspect-ratio correction.
  const halfHeightM =
    ((box.upMax - box.upMin) / 2 / MODEL_UNITS_PER_METRE) * ZEPPELIN_GASBAG_SCALE;
  return {
    x: (block[0] / MODEL_UNITS_PER_METRE) * ZEPPELIN_GASBAG_SCALE,
    y: fraction.up * halfHeightM,
    z: -fraction.forward * halfLengthM,
  };
}

/** One `MG.DAT` station as a `GunMountSpec`, the same shape `aircraft-spec.ts` builds for an aeroplane. */
function buildMount(block: GunBlockRow, index: number): GunMountSpec {
  const [rightUnits, forwardUnits, upUnits, mountClassRaw, verticalFromBam, verticalToBam, horizontalFromBam, horizontalToBam] =
    block;
  return {
    index,
    mountClassRaw,
    barrels: barrelsForMountClass(mountClassRaw),
    rightUnits,
    forwardUnits,
    upUnits,
    bodyOffsetM: zeppelinMountOffsetM(block),
    verticalFromBam,
    verticalToBam,
    horizontalFromBam,
    horizontalToBam,
    elevationFromRad: bamToRadians(verticalFromBam),
    elevationToRad: bamToRadians(verticalToBam),
    azimuthFromRad: bamToRadians(horizontalFromBam),
    azimuthToRad: bamToRadians(horizontalToBam),
    wrapsRear: horizontalFromBam > horizontalToBam,
    // Every one of the four is a gunner's station and the arcs say so without
    // being told: 1 and 3 wrap through the rear, 0 and 2 carry elevation bands
    // 12000 and 9000 counts wide against a fixed gun's 1000. `hasFixedGun`
    // below is therefore false, which is what makes the blind spot a *hole*
    // rather than somewhere the boresight quietly still reaches.
    flexible: true,
  };
}

const MOUNTS: readonly GunMountSpec[] = ZEPPELIN_GUN_BLOCKS.map(buildMount);

/**
 * The airship's `GunSpec` — the same record an aeroplane's guns are described
 * by, so `traverse.ts` and `gunnery.ts` need no idea that the thing holding it
 * has no `SimState`.
 *
 * `hasFixedGun: false` and `forwardMount` set to station 0 follow the five
 * gunner-armed aeroplanes (`BE2`, `AVIATIK`, `FE2`, `GOTHA`, `HANDLEY`): with
 * no fixed gun, `bearingsFor` returns an empty list when nothing bears, and an
 * empty list is the blind spot.
 *
 * `roundsPerGun` is `MG.DAT`'s own 120 rather than the fleet's 500, and
 * `roundsPerSecond` is the AI's share of the player's rate. Neither reaches
 * `flight/guns.ts` — an airship has no `GunState`, no burst counter and no jam
 * machine — they are carried here so one record answers for the whole weapon.
 */
export function airshipGunSpec(roundsPerSecond: number): GunSpec {
  return {
    positions: MOUNTS.length,
    mounts: MOUNTS,
    forwardMount: MOUNTS[0]!,
    hasFixedGun: false,
    barrels: MOUNTS[0]!.barrels,
    damageRaw: ZEPPELIN_GUN_EFFECTIVENESS,
    // Reference data, unconsumed — see `ZEPPELIN_JAM_THRESHOLD_TABLE`. The
    // scalar is `MG.DAT` `0x1C`, which per §5 the engine overwrites at mission
    // load anyway.
    jamThresholdRaw: ZEPPELIN_JAM_THRESHOLD_TABLE[0],
    jamThresholdTable: ZEPPELIN_JAM_THRESHOLD_TABLE,
    roundsPerGun: ZEPPELIN_ROUNDS_PER_STATION,
    roundsPerSecond: roundsPerSecond * AIRSHIP_RATE_OF_FIRE_FRACTION,
  };
}

/**
 * The belts an armed target spawns with, one per station — what `createTarget`
 * puts in `gunRoundsRemaining`. Empty for every unarmed kind, and that emptiness
 * is the safety property: a balloon, a factory and a searchlight all get `[]`
 * and `airship-pass.ts` skips anything with no stations, so nothing that was
 * inert before this file existed has become dangerous.
 */
export function airshipStationBelts(kind: TargetKind): readonly number[] {
  if (!isArmedTargetKind(kind)) return [];
  return ZEPPELIN_GUN_BLOCKS.map(() => ZEPPELIN_ROUNDS_PER_STATION);
}
